import React, { useEffect } from "react";
import { Link } from "react-router-dom";

const pageStyle: React.CSSProperties = {
  minHeight: "100vh",
  background: "#f6f7f9",
  fontFamily:
    '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  color: "#111827",
};

const navStyle: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 10,
  background: "rgba(255,255,255,0.98)",
  borderBottom: "1px solid #e5e7eb",
  backdropFilter: "blur(10px)",
  padding: "0 24px",
  height: 56,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const brandStyle: React.CSSProperties = {
  fontFamily: '"Gotham", system-ui, sans-serif',
  fontWeight: 700,
  fontSize: 16,
  color: "#0f172a",
  textDecoration: "none",
};

const contentStyle: React.CSSProperties = {
  maxWidth: 720,
  margin: "0 auto",
  padding: "48px 24px 80px",
};

const h1Style: React.CSSProperties = {
  fontFamily: '"Gotham", system-ui, sans-serif',
  fontSize: 28,
  fontWeight: 700,
  color: "#0f172a",
  marginBottom: 8,
};

const lastUpdatedStyle: React.CSSProperties = {
  fontSize: 13,
  color: "#6b7280",
  marginBottom: 40,
};

const h2Style: React.CSSProperties = {
  fontFamily: '"Gotham", system-ui, sans-serif',
  fontSize: 18,
  fontWeight: 600,
  color: "#0f172a",
  marginTop: 36,
  marginBottom: 12,
};

const pStyle: React.CSSProperties = {
  fontSize: 14,
  lineHeight: 1.75,
  color: "#374151",
  marginBottom: 16,
};

const footerStyle: React.CSSProperties = {
  borderTop: "1px solid #e5e7eb",
  padding: "24px",
  textAlign: "center",
  fontSize: 13,
  color: "#6b7280",
};

const footerLinkStyle: React.CSSProperties = {
  color: "#0f172a",
  textDecoration: "none",
  fontWeight: 600,
  marginLeft: 8,
  marginRight: 8,
};

function TermsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={pageStyle}>
      {/* Navbar */}
      <nav style={navStyle}>
        <Link to="/" style={brandStyle}>
          Napkin
        </Link>
        <div style={{ display: "flex", gap: 16 }}>
          <Link
            to="/privacy"
            style={{ fontSize: 13, color: "#6b7280", textDecoration: "none" }}
          >
            Privacy Policy
          </Link>
          <Link
            to="/login"
            style={{ fontSize: 13, color: "#0f172a", textDecoration: "none", fontWeight: 600 }}
          >
            Log in
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main style={contentStyle}>
        <h1 style={h1Style}>Terms of Service</h1>
        <p style={lastUpdatedStyle}>Last updated: February 2026</p>

        <p style={pStyle}>
          Welcome to Napkin. These Terms of Service ("Terms") govern your access to and use of the
          Napkin platform, including our website, applications, and services (collectively, the
          "Service"). By accessing or using the Service, you agree to be bound by these Terms. If
          you do not agree, please do not use the Service.
        </p>

        <h2 style={h2Style}>1. Acceptance of Terms</h2>
        <p style={pStyle}>
          By creating an account or using the Service, you represent that you are at least 18 years
          old and have the legal capacity to enter into these Terms. If you are using the Service on
          behalf of an organization, you represent that you have the authority to bind that
          organization to these Terms.
        </p>

        <h2 style={h2Style}>2. Description of Service</h2>
        <p style={pStyle}>
          Napkin provides an electronic signature and document management platform that enables users
          to create, send, sign, and store legally binding agreements. The Service includes document
          creation tools, template management, electronic signature capture, recipient management,
          and document status tracking.
        </p>
        <p style={pStyle}>
          Electronic signatures executed through Napkin are intended to comply with applicable
          electronic signature laws, including the U.S. Electronic Signatures in Global and National
          Commerce Act (ESIGN) and the Uniform Electronic Transactions Act (UETA).
        </p>

        <h2 style={h2Style}>3. User Accounts and Responsibilities</h2>
        <p style={pStyle}>
          You are responsible for maintaining the confidentiality of your account credentials and for
          all activities that occur under your account. You agree to provide accurate, current, and
          complete information during registration and to update your information as necessary.
        </p>
        <p style={pStyle}>
          You agree not to: (a) share your account credentials with third parties; (b) use the
          Service for any unlawful purpose; (c) upload or transmit malicious code or content; (d)
          attempt to gain unauthorized access to other accounts or systems; or (e) interfere with the
          proper functioning of the Service.
        </p>

        <h2 style={h2Style}>4. Electronic Signatures and Legal Validity</h2>
        <p style={pStyle}>
          By using the Service to sign or request signatures on documents, you acknowledge that
          electronic signatures are legally binding and enforceable. Each signature captured through
          the Service includes a timestamp, signer identification, and an audit trail to support
          the validity and integrity of the signed document.
        </p>
        <p style={pStyle}>
          You are responsible for ensuring that your use of electronic signatures complies with
          applicable laws and regulations in your jurisdiction. Napkin does not provide legal advice
          and recommends consulting with a qualified attorney for specific legal questions.
        </p>

        <h2 style={h2Style}>5. Privacy and Data Handling</h2>
        <p style={pStyle}>
          Your privacy is important to us. Our collection, use, and protection of your personal
          information is governed by our{" "}
          <Link to="/privacy" style={{ color: "#2563eb", textDecoration: "underline" }}>
            Privacy Policy
          </Link>
          , which is incorporated into these Terms by reference. By using the Service, you consent to
          the data practices described in our Privacy Policy.
        </p>

        <h2 style={h2Style}>6. Intellectual Property</h2>
        <p style={pStyle}>
          The Service, including its design, features, code, and branding, is owned by Napkin and
          protected by copyright, trademark, and other intellectual property laws. You retain
          ownership of the content you upload or create through the Service. By uploading content,
          you grant Napkin a limited, non-exclusive license to store, process, and display that
          content solely to provide the Service to you.
        </p>

        <h2 style={h2Style}>7. Limitation of Liability</h2>
        <p style={pStyle}>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, NAPKIN SHALL NOT BE LIABLE FOR ANY INDIRECT,
          INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, OR
          GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE. NAPKIN'S TOTAL LIABILITY
          FOR ANY CLAIM ARISING FROM THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID TO NAPKIN IN
          THE TWELVE MONTHS PRECEDING THE CLAIM.
        </p>
        <p style={pStyle}>
          The Service is provided "as is" and "as available" without warranties of any kind, whether
          express or implied, including warranties of merchantability, fitness for a particular
          purpose, and non-infringement.
        </p>

        <h2 style={h2Style}>8. Termination</h2>
        <p style={pStyle}>
          You may terminate your account at any time by contacting us. We reserve the right to
          suspend or terminate your access to the Service at our discretion, including for violations
          of these Terms. Upon termination, your right to use the Service ceases immediately, though
          provisions that by their nature should survive will continue to apply.
        </p>

        <h2 style={h2Style}>9. Governing Law</h2>
        <p style={pStyle}>
          These Terms shall be governed by and construed in accordance with the laws of the State of
          Delaware, United States, without regard to its conflict of law provisions. Any disputes
          arising from these Terms or the Service shall be resolved in the state or federal courts
          located in Delaware.
        </p>

        <h2 style={h2Style}>10. Changes to Terms</h2>
        <p style={pStyle}>
          We may update these Terms from time to time. When we make material changes, we will notify
          you by email or by posting a notice on the Service. Your continued use of the Service after
          such changes constitutes your acceptance of the updated Terms.
        </p>

        <h2 style={h2Style}>11. Contact Information</h2>
        <p style={pStyle}>
          If you have any questions about these Terms, please contact us at:
        </p>
        <p style={pStyle}>
          <strong>Napkin</strong>
          <br />
          Email: legal@napkinsend.com
          <br />
          Website: napkinsend.com
        </p>
      </main>

      {/* Footer */}
      <footer style={footerStyle}>
        <span>&copy; {new Date().getFullYear()} Napkin. All rights reserved.</span>
        <br style={{ marginBottom: 8 }} />
        <Link to="/" style={footerLinkStyle}>
          Home
        </Link>
        <Link to="/privacy" style={footerLinkStyle}>
          Privacy Policy
        </Link>
      </footer>
    </div>
  );
}

export default TermsPage;
